import { skipToken } from '@tanstack/react-query';

import { type Leistung, type Veranstaltung, type Zeitraum } from '../../../../../dtos';
import { trpc } from '../../../../../trpc';
import { formatTimeRangeForDate } from '../../../../../utils';
import {
  getAllLeistungen,
  getSortedModulesForLeistung,
  type TeilleistungColumn,
} from '../../../../../utils/teilleistung';
import { AusschreibungRueckmeldungenCard } from '../../../Ausschreibung/AusschreibungRueckmeldungenCard';
import { AusschreibungsprozessCard } from '../../../Ausschreibung/AusschreibungsprozessCard';
import { FailedQueryAlert } from '../../../ErrorAlert';
import { type VorschlaegePruefenSelection } from '../VorschlaegePruefenCalendar.types.js';

export type { VorschlaegePruefenSelection };

type ExperteAntwortenProps = Readonly<{
  veranstaltung:
    (Pick<Veranstaltung, 'teilleistungen'> & Partial<Pick<Veranstaltung, 'akaDurchgangId' | 'ablauf'>>) | null;
  isVorschlaegePruefenMode?: boolean;
  vorschlagSelection?: VorschlaegePruefenSelection;
}>;

const formatAblaufTage = (ablauf: readonly Zeitraum[]) =>
  ablauf.map(({ start, end }) => formatTimeRangeForDate(start, end, true)).join(', ');

export const ExperteAntworten = function ({
  veranstaltung,
  isVorschlaegePruefenMode = false,
  vorschlagSelection,
}: ExperteAntwortenProps) {
  const akaDurchgangId = veranstaltung?.akaDurchgangId;
  const durchgangQuery = trpc.durchgang.getDurchgangByAkaDurchgangId.useQuery(
    akaDurchgangId ? { akaDurchgangId } : skipToken,
  );

  if (!veranstaltung) {
    return null;
  }

  if (durchgangQuery.isError) {
    return <FailedQueryAlert error={durchgangQuery.error} query="Durchgang" />;
  }

  const modultermine = akaDurchgangId ? durchgangQuery.data?.modultermine : undefined;
  const leistungen = modultermine
    ? getAllLeistungen(modultermine.flatMap((modultermin) => modultermin.teilleistungen))
    : getAllLeistungen(veranstaltung.teilleistungen);

  if (leistungen.length === 0) {
    return null;
  }

  const isPruefenMode = isVorschlaegePruefenMode;
  const datePerTeilleistung = new Map<string, string>(
    (modultermine ?? []).flatMap((modultermin) =>
      modultermin.teilleistungen.map(
        (teilleistung) => [teilleistung.teilleistungId, formatAblaufTage(teilleistung.ablauf)] as const,
      ),
    ),
  );
  const activeTeilleistungAblauf = veranstaltung.teilleistungen.find(
    (teilleistung) => teilleistung.teilleistungId === vorschlagSelection?.activeTeilleistungId,
  )?.ablauf;

  const getTeilleistungColumns = (leistungId: Leistung['leistungId']): TeilleistungColumn[] | undefined => {
    if (!modultermine) {
      return undefined;
    }

    const spalten = getSortedModulesForLeistung(modultermine, leistungId);

    return isPruefenMode
      ? spalten.map((spalte): TeilleistungColumn => ({
          ...spalte,
          date: datePerTeilleistung.get(spalte.teilleistungId),
        }))
      : spalten;
  };

  return (
    <>
      {leistungen.map((leistung) => (
        <div
          className={isPruefenMode ? 'grid grid-cols-1 gap-16' : 'grid grid-cols-1 gap-16 lg:grid-cols-[1fr_2fr]'}
          key={leistung.leistungId}
        >
          {!isPruefenMode && <AusschreibungsprozessCard leistung={leistung} showStatusActions={false} />}
          <AusschreibungRueckmeldungenCard
            leistung={leistung}
            teilleistungColumns={getTeilleistungColumns(leistung.leistungId)}
            showLeistungTag={isPruefenMode}
            date={
              isPruefenMode && !modultermine && activeTeilleistungAblauf
                ? formatAblaufTage(activeTeilleistungAblauf)
                : undefined
            }
            vorschlagSelection={vorschlagSelection?.leistungId === leistung.leistungId ? vorschlagSelection : undefined}
          />
        </div>
      ))}
    </>
  );
};
